import React, { useState } from 'react';
import { useForm } from '../../context/FormContext';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Building2, User, Search, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { titleCaseCompany } from '../../lib/nameCase';

const BUSINESS_TYPES = [
  { value: 'LimitedCompany', label: 'Private Limited Company' },
  { value: 'PLC', label: 'Public Limited Company' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'LLP', label: 'Limited Liability Partnership' },
  { value: 'Other', label: 'Other' }
];

const INDUSTRIES = [
  'Agriculture',
  'Automotive',
  'Construction',
  'Education',
  'Finance',
  'Healthcare',
  'Hospitality',
  'IT & Technology',
  'Legal',
  'Manufacturing',
  'Property',
  'Retail',
  'Transport & Logistics',
  'Other'
];

const PREMISES_TYPES = [
  'Leased',
  'Owned',
  'Home Based',
  'Virtual Office',
  'Other'
];

const COUNTRIES = [
  'United Kingdom',
  'England',
  'Scotland',
  'Wales',
  'Northern Ireland',
  'Republic of Ireland',
  'Other'
];

export const ApplicationTypeStep = () => {
  const { formData, updateFormData } = useForm();
  const [searchingPostcode, setSearchingPostcode] = useState(false);
  const [businessAddressOptions, setBusinessAddressOptions] = useState(null);
  
  const isBusinessApplication = formData.application_type === 'Business';

  const handleApplicationTypeChange = (type) => {
    updateFormData({ application_type: type });
  };

  const updateBusinessData = (field, value) => {
    updateFormData({
      business_details: {
        ...formData.business_details,
        [field]: value
      }
    });
  };

  const updateBusinessAddress = (field, value) => {
    updateFormData({
      business_details: {
        ...formData.business_details,
        business_address: {
          ...formData.business_details?.business_address,
          [field]: value
        }
      }
    });
  };

  const lookupBusinessPostcode = async () => {
    const postcode = formData.business_details?.business_address?.postcode?.trim();
    if (!postcode || postcode.length < 5) {
      toast.error('Please enter a valid postcode');
      return;
    }

    setSearchingPostcode(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/address-lookup/${encodeURIComponent(postcode)}`);
      if (response.data.success && response.data.addresses?.length > 0) {
        setBusinessAddressOptions(response.data.addresses);
        toast.success(`Found ${response.data.addresses.length} addresses`);
      } else {
        toast.error(response.data.error || 'No addresses found for this postcode');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Postcode lookup failed.';
      toast.error(msg);
    } finally {
      setSearchingPostcode(false);
    }
  };

  const selectBusinessAddress = (selectedAddress) => {
    updateFormData({
      business_details: {
        ...formData.business_details,
        business_address: {
          ...formData.business_details?.business_address,
          building: selectedAddress.building_number || selectedAddress.building_name || '',
          street: selectedAddress.street || '',
          town: selectedAddress.town || '',
          county: selectedAddress.county || ''
        }
      }
    });
    setBusinessAddressOptions(null);
    toast.success('Address selected');
  };

  return (
    <div className="space-y-8" data-testid="application-type-step">
      <div className="text-center">
        <div className="w-16 h-16 brand-bg-medium rounded-full hidden md:flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 brand-text" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Application Type
        </h2>
        <p className="text-slate-600 hidden md:block">Is this a personal or business application?</p>
      </div>

      {/* Application Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => handleApplicationTypeChange('Private')}
          className={`p-6 rounded-xl border-2 transition-all text-left ${
            formData.application_type !== 'Business'
              ? 'brand-border brand-bg-light'
              : 'border-slate-200 hover:border-slate-300'
          }`}
          data-testid="application-type-private"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              formData.application_type !== 'Business' ? 'brand-bg text-white' : 'bg-slate-100 text-slate-500'
            }`}>
              <User className="w-6 h-6" />
            </div>
            <div>
              <span className={`font-semibold text-lg block ${
                formData.application_type !== 'Business' ? 'brand-text' : 'text-slate-700'
              }`}>
                Private Individual
              </span>
              <span className="text-sm text-slate-500 mt-1 block">
                Including Sole Traders
              </span>
            </div>
          </div>
        </button>

        <button
          onClick={() => handleApplicationTypeChange('Business')}
          className={`p-6 rounded-xl border-2 transition-all text-left ${
            formData.application_type === 'Business'
              ? 'brand-border brand-bg-light'
              : 'border-slate-200 hover:border-slate-300'
          }`}
          data-testid="application-type-business"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              formData.application_type === 'Business' ? 'brand-bg text-white' : 'bg-slate-100 text-slate-500'
            }`}>
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <span className={`font-semibold text-lg block ${
                formData.application_type === 'Business' ? 'brand-text' : 'text-slate-700'
              }`}>
                Business
              </span>
              <span className="text-sm text-slate-500 mt-1 block">
                Limited Companies or Partnerships
              </span>
            </div>
          </div>
        </button>
      </div>

      {/* Business Details Form - Only shown when Business is selected */}
      {isBusinessApplication && (
        <div className="space-y-6 pt-6 border-t border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Business Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Business Type <span className="text-red-500">*</span>
              </Label>
              <select
                value={formData.business_details?.business_type || ''}
                onChange={(e) => updateBusinessData('business_type', e.target.value)}
                className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:brand-border focus:ring-1 brand-focus outline-none"
                data-testid="business-type-select"
              >
                <option value="">Please select...</option>
                {BUSINESS_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Industry <span className="text-red-500">*</span>
              </Label>
              <select
                value={formData.business_details?.industry || ''}
                onChange={(e) => updateBusinessData('industry', e.target.value)}
                className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:brand-border focus:ring-1 brand-focus outline-none"
                data-testid="industry-select"
              >
                <option value="">Please select...</option>
                {INDUSTRIES.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Business Name <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="Enter business name"
                value={formData.business_details?.business_name || ''}
                onChange={(e) => updateBusinessData('business_name', e.target.value)}
                className="h-12"
                data-testid="business-name-input"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Main Telephone
              </Label>
              <Input
                placeholder="e.g. 01234567890"
                value={formData.business_details?.main_telephone || ''}
                onChange={(e) => updateBusinessData('main_telephone', e.target.value.replace(/[\s\-().\\/]/g, ''))}
                className="h-12"
                data-testid="business-telephone-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Date of Incorporation
              </Label>
              <Input
                type="date"
                value={(() => {
                  const dateVal = formData.business_details?.date_of_incorporation || '';
                  // Convert DD/MM/YYYY to YYYY-MM-DD for input display
                  if (dateVal && dateVal.includes('/')) {
                    const parts = dateVal.split('/');
                    if (parts.length === 3) {
                      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                    }
                  }
                  return dateVal;
                })()}
                onChange={(e) => {
                  // Store as DD/MM/YYYY format
                  const isoDate = e.target.value;
                  if (isoDate) {
                    const parts = isoDate.split('-');
                    if (parts.length === 3) {
                      updateBusinessData('date_of_incorporation', `${parts[2]}/${parts[1]}/${parts[0]}`);
                    }
                  } else {
                    updateBusinessData('date_of_incorporation', '');
                  }
                }}
                className="h-12"
                data-testid="incorporation-date-input"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Previous Business Name
              </Label>
              <Input
                placeholder="If applicable"
                value={formData.business_details?.previous_business_name || ''}
                onChange={(e) => updateBusinessData('previous_business_name', e.target.value)}
                onBlur={(e) => {
                  const cased = titleCaseCompany(e.target.value);
                  if (cased !== e.target.value) updateBusinessData('previous_business_name', cased);
                }}
                className="h-12"
                data-testid="previous-business-name-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Registration Number
              </Label>
              <Input
                placeholder="Company registration number"
                value={formData.business_details?.registration_number || ''}
                onChange={(e) => updateBusinessData('registration_number', e.target.value)}
                className="h-12"
                data-testid="registration-number-input"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                VAT Number
              </Label>
              <Input
                placeholder="If VAT registered"
                value={formData.business_details?.vat_number || ''}
                onChange={(e) => updateBusinessData('vat_number', e.target.value)}
                className="h-12"
                data-testid="vat-number-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Country of Operation
              </Label>
              <select
                value={formData.business_details?.country_of_operation || ''}
                onChange={(e) => updateBusinessData('country_of_operation', e.target.value)}
                className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:brand-border focus:ring-1 brand-focus outline-none"
                data-testid="country-operation-select"
              >
                <option value="">Please select...</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Country of Incorporation
              </Label>
              <select
                value={formData.business_details?.country_of_incorporation || ''}
                onChange={(e) => updateBusinessData('country_of_incorporation', e.target.value)}
                className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:brand-border focus:ring-1 brand-focus outline-none"
                data-testid="country-incorporation-select"
              >
                <option value="">Please select...</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Business Description
            </Label>
            <Textarea
              placeholder="Brief description of the business activities"
              value={formData.business_details?.business_description || ''}
              onChange={(e) => updateBusinessData('business_description', e.target.value)}
              className="min-h-[80px]"
              data-testid="business-description-input"
            />
          </div>

          {/* Director Guarantor Checkbox - shown for Limited Company, PLC, LLP, Partnership */}
          {['LimitedCompany', 'PLC', 'LLP', 'Partnership'].includes(formData.business_details?.business_type) && (
            <div className="pt-4 border-t border-slate-200">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="director-guarantor-label">
                <input
                  type="checkbox"
                  checked={formData.business_details?.director_guarantor || false}
                  onChange={(e) => updateBusinessData('director_guarantor', e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 brand-text focus:ring-2 brand-focus"
                  data-testid="director-guarantor-checkbox"
                />
                <div>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                    Act as Guarantor if Required?
                  </span>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Director is willing to act as a personal guarantor for this finance agreement if required by the lender.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Business Address Section */}
          <h3 className="text-lg font-semibold text-slate-900 pt-4 border-t border-slate-200">Business Address</h3>
          
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Premises Type
            </Label>
            <select
              value={formData.business_details?.business_address?.premises_type || 'Leased'}
              onChange={(e) => updateBusinessAddress('premises_type', e.target.value)}
              className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:brand-border focus:ring-1 brand-focus outline-none"
              data-testid="premises-type-select"
            >
              <option value="">Please select...</option>
              {PREMISES_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Postcode <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. SW1A 1AA"
                  value={formData.business_details?.business_address?.postcode || ''}
                  onChange={(e) => updateBusinessAddress('postcode', e.target.value.toUpperCase())}
                  className="h-12 flex-1"
                  data-testid="business-postcode-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={lookupBusinessPostcode}
                  disabled={searchingPostcode}
                  className="h-12 px-4"
                  data-testid="lookup-business-postcode-btn"
                >
                  {searchingPostcode ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-1" />
                      Find
                    </>
                  )}
                </Button>
              </div>
              
              {businessAddressOptions && businessAddressOptions.length > 0 && (
                <div className="mt-2 rounded-lg border-2 border-blue-400 bg-blue-50/60 p-3 shadow-sm ring-2 ring-blue-200/70">
                  <Label className="text-sm font-semibold text-blue-800 mb-1 block">
                    Select your business address ({businessAddressOptions.length} found)
                  </Label>
                  <p className="text-xs text-blue-700/80 mb-2">
                    Please pick your address from the list to auto-fill the fields below.
                  </p>
                  <select
                    className="w-full h-11 px-3 rounded-lg border-2 border-blue-400 bg-white hover:border-blue-500 focus:border-blue-600 focus:ring-2 focus:ring-blue-300 outline-none text-sm"
                    defaultValue=""
                    onChange={(e) => {
                      const addr = businessAddressOptions[parseInt(e.target.value)];
                      if (addr) selectBusinessAddress(addr);
                    }}
                    data-testid="business-address-select"
                  >
                    <option value="" disabled>Tap to choose your address...</option>
                    {businessAddressOptions.map((addr, i) => (
                      <option key={i} value={i}>{addr.full}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Building
              </Label>
              <Input
                placeholder="Building name/number"
                value={formData.business_details?.business_address?.building || ''}
                onChange={(e) => updateBusinessAddress('building', e.target.value)}
                className="h-12"
                data-testid="business-building-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Street
              </Label>
              <Input
                placeholder="Street name"
                value={formData.business_details?.business_address?.street || ''}
                onChange={(e) => updateBusinessAddress('street', e.target.value)}
                className="h-12"
                data-testid="business-street-input"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Town
              </Label>
              <Input
                placeholder="Town/City"
                value={formData.business_details?.business_address?.town || ''}
                onChange={(e) => updateBusinessAddress('town', e.target.value)}
                className="h-12"
                data-testid="business-town-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                County
              </Label>
              <Input
                placeholder="County"
                value={formData.business_details?.business_address?.county || ''}
                onChange={(e) => updateBusinessAddress('county', e.target.value)}
                className="h-12"
                data-testid="business-county-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Years at Address
              </Label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                value={formData.business_details?.business_address?.years || ''}
                onChange={(e) => updateBusinessAddress('years', parseInt(e.target.value) || 0)}
                className="h-12"
                data-testid="business-address-years-input"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Months at Address
              </Label>
              <Input
                type="number"
                min="0"
                max="11"
                placeholder="0"
                value={formData.business_details?.business_address?.months || ''}
                onChange={(e) => updateBusinessAddress('months', parseInt(e.target.value) || 0)}
                className="h-12"
                data-testid="business-address-months-input"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationTypeStep;
